import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { employeeMatches } from "@/lib/employeeSearch";
import { toast } from "sonner";
import { Search, FileDown, Wallet, ArrowLeft } from "lucide-react";

function fmt(n) {
    if (n == null) return "₦0";
    return "₦" + Number(n).toLocaleString("en-NG", { maximumFractionDigits: 0 });
}

function periodLabel(p) {
    if (!p || p.length !== 7) return p || "—";
    const [y, m] = p.split("-");
    const month = new Date(+y, +m - 1, 1).toLocaleDateString("en-US", { month: "long" });
    return `${month} ${y}`;
}

export default function PayslipReport({ selfOnly = false }) {
    const { user } = useAuth();
    const role = user?.role || "";
    const showPicker = !selfOnly && role !== "employee";

    const [employees, setEmployees] = useState([]);
    const [selectedEmp, setSelectedEmp] = useState(selfOnly ? user?.employee_id : "");
    const [empSearch, setEmpSearch] = useState("");
    const [ledger, setLedger] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState("");

    useEffect(() => {
        if (!showPicker) return;
        (async () => {
            try {
                const { data } = await api.get("/employees");
                setEmployees(data || []);
            } catch {
                // silently ignore
            }
        })();
    }, [showPicker]);

    const loadLedger = async (empId) => {
        if (!empId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/employees/${empId}/payslip-ledger`);
            setLedger(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load payslips");
            setLedger(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const id = selfOnly ? user?.employee_id : selectedEmp;
        if (!id) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/employees/${id}/payslip-ledger`);
                if (!cancelled) setLedger(data);
            } catch (e) {
                if (!cancelled) {
                    toast.error(formatApiError(e.response?.data?.detail) || "Failed to load payslips");
                    setLedger(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selfOnly, user?.employee_id, selectedEmp]);

    const empQ = empSearch.trim().toLowerCase();
    const filteredEmps = empQ
        ? employees.filter((e) => employeeMatches(e, empQ)).slice(0, 50)
        : employees.slice(0, 50);

    const downloadPDF = async (runId, itemId, period, name) => {
        setDownloadingId(itemId);
        try {
            const res = await api.get(`/payroll/${runId}/payslip/${itemId}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `payslip_${period}_${(name || "employee").replace(/\s+/g, "_")}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not download payslip");
        } finally {
            setDownloadingId("");
        }
    };

    const headerTitle = selfOnly ? "My Payslips" : "Payslip Report";
    const headerSubtitle = selfOnly
        ? "Your full payslip history. Tap Download PDF for any individual payslip."
        : "View the payslip history of any employee. Tap Download PDF for an individual record.";

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="payslip-report-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Compensation</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02] flex items-center gap-3">
                        <Wallet className="w-9 h-9 text-klein" strokeWidth={1.5} /> {headerTitle}
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-2xl">{headerSubtitle}</p>
                </div>
            </div>

            {/* Employee picker (HR/Admin) */}
            {showPicker && (
                <div className="mt-10 bg-white border border-ink/10 p-5" data-testid="payslip-employee-picker">
                    <label className="overline block mb-2">Choose employee</label>
                    {selectedEmp && ledger?.employee ? (
                        <div className="flex items-center justify-between border border-klein bg-klein/5 px-3 py-2.5">
                            <div className="text-sm">
                                <div className="font-medium text-ink">{ledger.employee.full_name}</div>
                                <div className="text-xs text-ink/60">{ledger.employee.employee_no} · {ledger.employee.department || "—"} · {ledger.employee.position || "—"}</div>
                            </div>
                            <button
                                onClick={() => { setSelectedEmp(""); setLedger(null); }}
                                className="text-xs uppercase tracking-[0.12em] text-ink/60 hover:text-ink"
                                data-testid="payslip-employee-clear"
                            >
                                Change <ArrowLeft className="w-3 h-3 inline rotate-180" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                                <input
                                    type="search"
                                    value={empSearch}
                                    onChange={(e) => setEmpSearch(e.target.value)}
                                    placeholder="Search name, staff no, department, division, phone…"
                                    className="w-full border border-ink/15 py-2.5 pl-9 pr-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="payslip-employee-search"
                                />
                            </div>
                            <div className="mt-2 max-h-64 overflow-y-auto border border-ink/10" data-testid="payslip-employee-list">
                                {filteredEmps.length === 0 ? (
                                    <div className="px-3 py-4 text-xs text-ink/40">No matches.</div>
                                ) : filteredEmps.map((e) => (
                                    <button
                                        key={e.id}
                                        onClick={() => setSelectedEmp(e.id)}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-bone border-b border-ink/5 last:border-b-0"
                                        data-testid={`payslip-employee-option-${e.id}`}
                                    >
                                        <div className="font-medium text-ink">{e.first_name} {e.last_name}</div>
                                        <div className="text-xs text-ink/50">{e.employee_no || "—"} · {e.department || "—"}</div>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Ledger */}
            {loading && <div className="mt-10 text-sm text-ink/40">Loading payslips…</div>}
            {!loading && ledger && (
                <div className="mt-10" data-testid="payslip-ledger">
                    {selfOnly && (
                        <div className="bg-white border border-ink/10 p-6 mb-6" data-testid="payslip-self-header">
                            <div className="overline">Employee</div>
                            <div className="mt-2 font-display font-bold text-xl text-ink">{ledger.employee.full_name}</div>
                            <div className="text-sm text-ink/60">{ledger.employee.employee_no} · {ledger.employee.department || "—"} · {ledger.employee.position || "—"}</div>
                        </div>
                    )}
                    {ledger.payslips.length === 0 ? (
                        <div className="bg-white border border-ink/10 p-12 text-center text-sm text-ink/40" data-testid="payslip-empty">
                            No payslips yet. They appear here as soon as the first payroll run includes this employee.
                        </div>
                    ) : (
                        <div className="bg-white border border-ink/10 overflow-auto">
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr className="border-b-2 border-ink/20 bg-bone">
                                        <th className="text-left overline px-4 py-3">Period</th>
                                        <th className="text-left overline px-4 py-3">Status</th>
                                        <th className="text-right overline px-4 py-3">Gross</th>
                                        <th className="text-right overline px-4 py-3 hidden md:table-cell">PAYE</th>
                                        <th className="text-right overline px-4 py-3 hidden md:table-cell">Pension</th>
                                        <th className="text-right overline px-4 py-3 hidden lg:table-cell">Deductions</th>
                                        <th className="text-right overline px-4 py-3 bg-klein text-white">Net pay</th>
                                        <th className="text-right overline px-4 py-3">Payslip</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ledger.payslips.map((p) => {
                                        const totalDed = (p.paye || 0) + (p.pension || 0) + (p.loan_deduction || 0) + (p.other_deductions || 0);
                                        return (
                                            <tr key={p.id} className="border-b border-ink/10 hover:bg-bone/40" data-testid={`payslip-row-${p.id}`}>
                                                <td className="px-4 py-3 font-medium text-ink">{periodLabel(p.period)}</td>
                                                <td className="px-4 py-3">
                                                    <span className="inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.12em] bg-ink/5 border-ink/10 text-ink/70">{p.run_status || "—"}</span>
                                                </td>
                                                <td className="px-4 py-3 text-right tabular-nums">{fmt(p.gross_salary)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell">{fmt(p.paye)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell">{fmt(p.pension)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums hidden lg:table-cell text-ink/70">{fmt(totalDed)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums font-display font-bold text-klein">{fmt(p.net_pay)}</td>
                                                <td className="px-4 py-3 text-right">
                                                    <button
                                                        onClick={() => downloadPDF(p.run_id, p.id, p.period, ledger.employee.full_name)}
                                                        disabled={downloadingId === p.id}
                                                        className="inline-flex items-center gap-1 text-xs text-klein hover:text-ink disabled:opacity-50"
                                                        data-testid={`payslip-download-${p.id}`}
                                                    >
                                                        <FileDown className="w-3.5 h-3.5" />
                                                        {downloadingId === p.id ? "…" : "Download PDF"}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
            {!loading && !ledger && !showPicker && selfOnly && (
                <div className="mt-10 text-sm text-ink/40">Loading your payslips…</div>
            )}
        </div>
    );
}
